'use client';

import * as React from 'react';
import { Progress as ProgressPrimitive } from 'radix-ui';

import { cn } from '../lib/utils';

/**
 * `value` clamped to [0, max]; null (indeterminate) when it is not a finite
 * number. Radix logs a console error for anything outside that range, and
 * the app forwards console errors to Sentry, while callers legitimately pass
 * ratios that overshoot (more assignments than slots, paid above revenue).
 */
function clampValue(value: number | null | undefined, max: number) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.min(Math.max(value, 0), max);
}

const Progress: React.FC<
  React.ComponentProps<typeof ProgressPrimitive.Root>
> = ({ className, value, max, ...props }) => {
  const limit = typeof max === 'number' && max > 0 ? max : 100;
  const clamped = clampValue(value, limit);
  return (
    <ProgressPrimitive.Root
      className={cn(
        'bg-primary/20 relative h-2 w-full overflow-hidden rounded-full',
        className,
      )}
      // Radix derives aria-valuenow and data-state from `value`; without it
      // every bar reads as indeterminate to assistive technology.
      value={clamped}
      max={limit}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className="bg-primary h-full w-full flex-1 transition-all"
        style={{
          transform: `translateX(-${100 - ((clamped ?? 0) / limit) * 100}%)`,
        }}
      />
    </ProgressPrimitive.Root>
  );
};

Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
